import { fmtNum, lei, roDateTime, siteLabel, type MarketProduct, t } from '@procumi/shared';
import { useState } from 'react';
import { useAsync } from '../../hooks/useAsync';
import { api } from '../../lib/api';
import type { Route } from '../../routes';
import { PriceChart } from '../PriceChart';
import { ErrorBox, Loading } from '../StateBox';
import { day, pct, price, Supplier } from './PriceMarks';

/**
 * Rândul deschis: clasamentul de la ultima cerere și răspunsul la „de la cine
 * să comand”. Regula, hotărâtă cu proprietarul: cel mai mic preț la ultima
 * cerere, la egalitate cel care a câștigat de mai multe ori. Termenul de
 * livrare și plata nu intră.
 */
export function PriceVerdict({
  p,
  slots,
  periodText,
  go,
}: {
  p: MarketProduct;
  slots: Record<string, number>;
  periodText: string;
  go: (r: Route) => void;
}) {
  const [history, setHistory] = useState(false);
  const worst = p.rank[p.rank.length - 1]!.price;
  const n = p.points.length;
  const won = p.wins.find((w) => w.supplier.id === p.pick.id)?.count ?? 0;
  const unit = `lei/${p.unit}`;

  return (
    <div className="pv">
      <div className="pv-grid">
        <div className="pv-panel">
          <h4>{t('Clasamentul la cererea din {0}', day(p.last.at))}</h4>
          {!p.last.complete ? (
            <p className="pv-wait">
              {t('{0} din {1} oferte primite, se mai așteaptă', p.last.received, p.last.invited)}
            </p>
          ) : null}
          <ol className="pv-rank">
            {p.rank.map((r, i) => (
              <li key={r.supplier.id}>
                <span className="i">{i + 1}</span>
                <Supplier s={r.supplier} slots={slots} />
                <span className="bar" aria-hidden="true">
                  <i
                    className={r.best ? 'best' : ''}
                    style={{
                      width: `${30 + (worst === p.now ? 0 : (70 * (r.price - p.now)) / (worst - p.now))}%`,
                    }}
                  />
                </span>
                <span className="v">
                  <b>{price(r.price)}</b> <span className="u">{unit}</span>
                </span>
                <span className="d">{r.best ? t('cel mai mic') : pct(r.diff)}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="pv-panel pv-verdict">
          <span className="pv-k">{t('De la cine să comanzi')}</span>
          <div className="pv-who">
            <Supplier s={p.pick} slots={slots} />
          </div>
          <ul>
            <li>
              {p.last.complete
                ? t('Cel mai mic preț la ultima cerere')
                : t('Până acum, cel mai mic preț e la el')}
              :{' '}
              <b>
                {price(p.now)} {unit}
              </b>
            </li>
            {p.ties.length ? (
              <li>{t('Același preț ca {0}', p.ties.map((s) => s.name).join(', '))}</li>
            ) : p.next ? (
              <li>{t('Cu {0} {1} sub {2}', fmtNum(p.next.price - p.now), unit, p.next.supplier.name)}</li>
            ) : null}
            <li>
              {p.inPeriod
                ? n === 1
                  ? t('Cel mai ieftin la {0} din {1} cerere {2}', won, n, periodText)
                  : t('Cel mai ieftin la {0} din {1} cereri {2}', won, n, periodText)
                : t('Nicio cerere {0}; ultima e din {1}', periodText, day(p.last.at))}
            </li>
          </ul>
          <div className="pv-actions">
            <button
              type="button"
              className="btn primary"
              onClick={() => go({ view: 'rfq', id: p.last.rfqId })}
            >
              {t('Deschide cererea din {0}', day(p.last.at))}
            </button>
            <button
              type="button"
              className="btn"
              aria-expanded={history}
              onClick={() => setHistory((h) => !h)}
            >
              {history ? t('Ascunde istoricul') : t('Tot istoricul')}
            </button>
          </div>
        </div>
      </div>
      {history ? <History keyOf={p.key} unit={p.unit} go={go} /> : null}
    </div>
  );
}

/** Toate prețurile materialului: graficul și tabelul de dinainte. */
function History({ keyOf, unit, go }: { keyOf: string; unit: string; go: (r: Route) => void }) {
  const { data, error, loading, reload } = useAsync(() => api.price(keyOf), [keyOf], `price:${keyOf}`);
  if (loading) return <Loading text={t('Se încarcă istoricul…')} />;
  if (error || !data) return <ErrorBox error={error ?? t('Nu am putut încărca.')} onRetry={reload} />;
  return (
    <div className="pv-history">
      <PriceChart quotes={data.quotes} unit={unit} />
      <div className="tbl-wrap">
        <table className="sups">
          <thead>
            <tr>
              <th>{t('Data')}</th>
              <th>{t('Furnizor')}</th>
              <th>{t('Cerere')}</th>
              <th style={{ textAlign: 'right' }}>{t('Cantitate')}</th>
              <th style={{ textAlign: 'right' }}>{t('Preț unitar')}</th>
              <th style={{ textAlign: 'right' }}>{t('Valoare')}</th>
            </tr>
          </thead>
          <tbody>
            {data.quotes.map((q, i) => (
              <tr key={i}>
                <td>{roDateTime(q.at)}</td>
                <td>{q.supplier.name}</td>
                <td>
                  <button type="button" className="link" onClick={() => go({ view: 'rfq', id: q.rfqId })}>
                    {q.rfqTitle}
                  </button>
                  <div className="sub muted" style={{ fontSize: 13 }}>
                    {siteLabel(q.site)}
                  </div>
                </td>
                <td style={{ textAlign: 'right' }}>
                  {fmtNum(q.qty)} {q.unit}
                </td>
                <td style={{ textAlign: 'right' }}>{fmtNum(q.price)}</td>
                <td style={{ textAlign: 'right' }}>{lei(q.price * q.qty)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
